import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { AggregatedQueryProps } from "@/api/utils/QueryAggregator"
import dayjs from "dayjs"

import { JuniferService } from "@/services/JuniferService"
import { AccountService } from "@/services/AccountService"
import { getAccountContacts } from "./getAccountContacts"
import { getLegacyFreshdeskTickets } from "./getFreshdeskTickets"
import { NoteService } from "@/services/NotesService"
import { TransactionService } from "@/services/TransactionService"
import { ITransaction } from "@/models/transaction/transaction.types"
import { IFreshdeskTicket } from "@/models/freshdeskTicket/freshdeskTicket.types"
import { IJuniferComm } from "@/models/juniferCommunication/juniferComm.types"
import { IJuniferTicket } from "@/models/juniferTickets/juniferTicket.types"
import { INote } from "@/models/note/note.types"
import { CommunicationsService } from "@/services/CommunicationsService"
import { MeterAppointmentService } from "@/services/MeterAppointmentService"
import {
  ActivityFeedItemType,
  FreshdeskTicketFeedItem,
  IItemsByDate,
  JuniferCommFeedItem,
  JuniferTicketFeedItem,
  NoteFeedItem,
  TransactionFeedItem,
  CommEmailFeedItem,
  CaseFeedItem,
  ComplaintFeedItem,
  MeterAppointmentFeedItem,
} from "@/models/activityFeed/ActivityFeedItem"
import { CasesService } from "@/services/CasesService"
import { ComplaintService } from "@/services/ComplaintService"
import { DcaConfigurationService } from "@/services/DcaConfigurationService"

export interface FeedItems {
  juniferComms: IJuniferComm[]
  juniferTickets: IJuniferTicket[]
  freshdeskTickets: IFreshdeskTicket[]
  notes: INote[]
  transactions: ITransaction[]
}

export const getActivityFeedEvents = (
  accountNumber: string,
): AggregatedQueryProps<IItemsByDate[]> => {
  const queries = new QueryAggregator()

  const coreId = AccountService.getCoreIdByAccountNumber(accountNumber)
  const juniferCommQuery = JuniferService.getCommsForAccount(coreId.data)
  const juniferTicketsQuery = JuniferService.getTicketsForAccount(coreId.data)
  const legacyFreshdeskTicketsQuery = getLegacyFreshdeskTickets(accountNumber)
  const notesQuery = NoteService.getForAccount(coreId.data)
  const transactionsQuery = TransactionService.getForAccount(coreId.data)

  const accountContacts = getAccountContacts(accountNumber)
  const primaryContact = accountContacts?.data?.find((contact) => {
    return contact?.juniferPrimaryContact
  })
  const emailQuery = CommunicationsService.getEmails(primaryContact?.email)
  const cases = CasesService.getCasesForAccount(coreId.data)
  const complaints = ComplaintService.getForAccount(accountNumber)
  const meterAppointmentsQuery = MeterAppointmentService.getAppointments(
    coreId.data,
  )
  const dcaNames = (DcaConfigurationService.getConfiguration().data ?? []).map(
    (dca) => dca.name,
  )

  queries.add(coreId)
  // Junifer comms aren't aggregated: Junifer can take several seconds, and the
  // rest of the feed shouldn't wait for it. Comms join the feed when they land.
  queries.add(juniferTicketsQuery)
  queries.add(legacyFreshdeskTicketsQuery)
  queries.add(notesQuery)
  queries.add(transactionsQuery)
  queries.add(emailQuery)
  queries.add(cases)
  queries.add(complaints)
  queries.add(meterAppointmentsQuery)

  const itemsList: ActivityFeedItemType[] = [
    ...juniferCommQuery.data.map((item) => {
      return new JuniferCommFeedItem(item)
    }),
    ...(juniferTicketsQuery.data ?? [])
      .filter((ticket) => !ticket.keyIdentifier.includes("COMPLAINTS-TICKET"))
      .map((item) => {
        return new JuniferTicketFeedItem(item, dcaNames)
      }),
    ...legacyFreshdeskTicketsQuery.data.map((item) => {
      return new FreshdeskTicketFeedItem(item)
    }),
    ...(notesQuery.data ?? []).map((item) => {
      return new NoteFeedItem(item, dcaNames)
    }),
    ...transactionsQuery.data.map((item) => {
      return new TransactionFeedItem(item)
    }),
    ...emailQuery.data.map((item) => {
      return new CommEmailFeedItem(item)
    }),
    ...cases.data.map((item) => {
      return new CaseFeedItem(item)
    }),
    ...complaints.data.map((item) => {
      return new ComplaintFeedItem(item)
    }),
    ...(meterAppointmentsQuery.data ?? []).map((item) => {
      return new MeterAppointmentFeedItem(item)
    }),
  ]

  return queries.setData(sortedFeedItems(itemsList)).getAggregate()
}

export function sortDayEvents(events: ActivityFeedItemType[]) {
  return [...events].sort(
    (date1, date2) => date2.createdDate.valueOf() - date1.createdDate.valueOf(),
  )
}

export const sortedFeedItems = (
  feedItems: ActivityFeedItemType[],
): IItemsByDate[] => {
  const result = feedItems.reduce(function (
    arr: {
      [key: string]: ActivityFeedItemType[]
    },
    item,
  ) {
    ;(arr[item.createdDate.format("ddd MMM D YYYY")] ||= []).push(item)
    return arr
  }, {})

  const itemsByDate = Object.keys(result).map((date) => {
    return {
      date,
      items: sortDayEvents(result[date]),
    }
  })

  return itemsByDate.sort((a, b) => {
    return dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
  })
}
